// src/hooks/useWishlist.js
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

import { useConfiguration } from '../context/configurationContext';
import { useRouteConfiguration } from '../context/routeConfigurationContext';

import { currentUserSelector } from '../ducks/user.duck';
import { selectIsAuthenticated } from '../ducks/auth.duck';
import { currentFavoriteIdSelector } from '../containers/FavoritesPage/FavoritesPage.duck';

import {
  addItemToFavorites,
  deleteItemFromFavorites,
} from '../containers/FavoritesPage/FavoritesPage.duck';

import { retrieveUserWishlist, getLikes } from '../util/dataExtractor';
import { isArrayLength } from '../util/genericHelpers';
import { showErrorToast } from '../util/toast';

/**
 * Reusable wishlist hook for listings.
 *
 * @param {object} listing - ensureListing(listing) recommended
 * @returns {object} { isInWishlist, isProcessing, toggleWishlist, iconName, isOwnListing }
 */
export default function useWishlist(listing) {
  const dispatch = useDispatch();
  const history = useHistory();
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();

  const currentUser = useSelector(currentUserSelector);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const currentFavoriteId = useSelector(currentFavoriteIdSelector);

  const listingId = listing?.id?.uuid;
  const likes = useMemo(() => getLikes(listing), [listing]);

  const currentUserId = currentUser?.id?.uuid;
  const isOwnListing =
    !!currentUser?.id && listing?.author?.id?.uuid === currentUser?.id?.uuid;

  const favoritesList = retrieveUserWishlist(currentUser);
  const isInWishlist =
    isArrayLength(favoritesList) && favoritesList.includes(listingId);

  const isProcessing = currentFavoriteId === listingId;

  const toggleWishlist = useCallback(
    async (e) => {
      if (e?.preventDefault) e.preventDefault();
      if (e?.stopPropagation) e.stopPropagation();

      if (!listingId) return;

      try {
        if (isInWishlist) {
          await dispatch(
            deleteItemFromFavorites({
              isAuthenticated,
              id: listingId,
              dispatch,
              favorites: favoritesList,
              config,
              likes,
              userId: currentUserId,
            })
          );
        } else {
          await dispatch(
            addItemToFavorites({
              favorites: favoritesList,
              currentFavoriteId: listingId,
              userId: currentUserId,
              likes,
              isAuthenticated,
              config,
              history,
              routeConfiguration,
            })
          );
        }
      } catch (err) {
        showErrorToast('Failed to update wishlist');
      }
    },
    [
      dispatch,
      favoritesList,
      isAuthenticated,
      isInWishlist,
      listingId,
      currentUserId,
      likes,
      config,
      history,
      routeConfiguration,
    ]
  );

  const iconName = isInWishlist ? 'favorite' : 'unfavorite';

  return { isInWishlist, isProcessing, toggleWishlist, iconName, isOwnListing };
}
